import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  LinearProgress,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import CategoryRoundedIcon from '@mui/icons-material/CategoryRounded';
import Inventory2RoundedIcon from '@mui/icons-material/Inventory2Rounded';
import NotesRoundedIcon from '@mui/icons-material/NotesRounded';
import RestartAltRoundedIcon from '@mui/icons-material/RestartAltRounded';
import SaveRoundedIcon from '@mui/icons-material/SaveRounded';
import TagRoundedIcon from '@mui/icons-material/TagRounded';
import VerifiedRoundedIcon from '@mui/icons-material/VerifiedRounded';
import { toast } from 'react-toastify';

import api from '../../services/api';
import { DetailField, PageHeader, PageLayout, SectionCard } from '../../components/ui';

const initialAsset = {
  asset_tag: '',
  asset_type: '',
  serial_number: '',
  condition: 'new',
  status: 'available',
  notes: '',
};

const ASSET_TYPES = [
  { value: 'laptop', label: 'Laptop' },
  { value: 'phone', label: 'Phone' },
  { value: 'vehicle', label: 'Vehicle' },
  { value: 'equipment', label: 'Equipment' },
];

const CONDITIONS = [
  { value: 'new', label: 'New' },
  { value: 'used', label: 'Used' },
  { value: 'damaged', label: 'Damaged' },
];

const titleize = (value) =>
  String(value || '')
    .replace(/[_-]/g, ' ')
    .replace(/\b\w/g, (match) => match.toUpperCase())
    .trim();

function FieldGroup({ icon, title, description, children }) {
  return (
    <Box>
      <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ mb: 1 }}>
        {icon}
        <Box>
          <Typography variant="subtitle2" sx={{ fontWeight: 900 }}>
            {title}
          </Typography>
          {description ? (
            <Typography variant="caption" color="text.secondary">
              {description}
            </Typography>
          ) : null}
        </Box>
      </Stack>
      {children}
    </Box>
  );
}

function FieldGrid({ children }) {
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
        gap: 1.2,
      }}
    >
      {children}
    </Box>
  );
}

function SummaryRow({ icon, label, value }) {
  return (
    <Stack direction="row" spacing={1} alignItems="flex-start">
      <Box sx={{ color: 'text.secondary', display: 'grid', placeItems: 'center', mt: 0.1 }}>{icon}</Box>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 800 }}>
          {label}
        </Typography>
        <Typography variant="body2" sx={{ fontWeight: 850, overflowWrap: 'anywhere' }}>
          {value || '-'}
        </Typography>
      </Box>
    </Stack>
  );
}

export default function AddAssetPage() {
  const navigate = useNavigate();
  const theme = useTheme();
  const [asset, setAsset] = useState(initialAsset);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const requiredStatus = useMemo(
    () => [
      { label: 'Asset tag', done: Boolean(asset.asset_tag.trim()) },
      { label: 'Asset type', done: Boolean(asset.asset_type) },
      { label: 'Serial number', done: Boolean(asset.serial_number.trim()) },
    ],
    [asset.asset_tag, asset.asset_type, asset.serial_number]
  );
  const completionPct = Math.round((requiredStatus.filter((item) => item.done).length / requiredStatus.length) * 100);

  const fieldSx = {
    '& .MuiOutlinedInput-root': {
      borderRadius: theme.hr?.radius?.md || 2,
      backgroundColor: alpha(theme.palette.background.paper, theme.palette.mode === 'dark' ? 0.74 : 0.92),
    },
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    setAsset((current) => ({ ...current, [name]: value }));
  };

  const handleReset = () => {
    setError('');
    setAsset(initialAsset);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');
    setSaving(true);
    try {
      await api.post('/assets', asset);
      toast.success('Asset saved successfully.');
      navigate('/assets');
    } catch (err) {
      console.error('Add asset error:', err);
      setError(err.response?.data?.message || 'Error while saving.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <PageLayout backgroundAccent compact maxWidth="xl">
      <PageHeader
        compact
        variant="dashboard"
        overline="Inventory"
        title="Add New Asset"
        subtitle="Register hardware, vehicles, and equipment with clean ownership and condition metadata."
        actions={[
          <Button
            key="back"
            variant="outlined"
            startIcon={<ArrowBackRoundedIcon />}
            onClick={() => navigate('/assets')}
          >
            Back to Assets
          </Button>,
        ]}
      />

      <Box
        component="form"
        onSubmit={handleSubmit}
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 1fr) 360px' },
          gap: 1.5,
          alignItems: 'start',
        }}
      >
        <SectionCard
          tone="dashboard"
          title="Asset Details"
          subheader="Required fields identify the asset and make it searchable in the inventory."
          contentSx={{ p: { xs: 1.35, md: 1.7 } }}
        >
          <Stack spacing={1.8}>
            {error ? <Alert severity="error">{error}</Alert> : null}

            <FieldGroup
              icon={<Inventory2RoundedIcon color="primary" />}
              title="Identity"
              description="Use a unique tag and serial number for audit-friendly asset tracking."
            >
              <FieldGrid>
                <TextField
                  required
                  label="Asset Tag"
                  name="asset_tag"
                  value={asset.asset_tag}
                  onChange={handleChange}
                  sx={fieldSx}
                />
                <TextField
                  required
                  label="Serial Number"
                  name="serial_number"
                  value={asset.serial_number}
                  onChange={handleChange}
                  sx={fieldSx}
                />
              </FieldGrid>
            </FieldGroup>

            <Divider />

            <FieldGroup
              icon={<CategoryRoundedIcon color="primary" />}
              title="Classification"
              description="Choose the asset family and current condition before assigning it later."
            >
              <FieldGrid>
                <TextField
                  required
                  select
                  label="Asset Type"
                  name="asset_type"
                  value={asset.asset_type}
                  onChange={handleChange}
                  sx={fieldSx}
                >
                  {ASSET_TYPES.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  select
                  label="Condition"
                  name="condition"
                  value={asset.condition}
                  onChange={handleChange}
                  sx={fieldSx}
                >
                  {CONDITIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </TextField>
              </FieldGrid>
            </FieldGroup>

            <Divider />

            <FieldGroup
              icon={<NotesRoundedIcon color="primary" />}
              title="Notes"
              description="Add purchase context, accessory details, or handover notes."
            >
              <TextField
                multiline
                minRows={4}
                label="Notes"
                name="notes"
                value={asset.notes}
                onChange={handleChange}
                sx={fieldSx}
              />
            </FieldGroup>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="flex-end">
              <Button
                type="button"
                variant="outlined"
                startIcon={<RestartAltRoundedIcon />}
                onClick={handleReset}
                disabled={saving}
              >
                Reset
              </Button>
              <Button
                type="submit"
                variant="contained"
                startIcon={<SaveRoundedIcon />}
                disabled={saving}
              >
                {saving ? 'Saving...' : 'Save Asset'}
              </Button>
            </Stack>
          </Stack>
        </SectionCard>

        <Stack spacing={1.5} sx={{ position: { lg: 'sticky' }, top: { lg: 88 } }}>
          <SectionCard
            tone="dashboard"
            title="Inventory Summary"
            subheader="Live preview before creating the record."
            contentSx={{ p: 1.6 }}
          >
            <Stack spacing={1.25}>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <Chip
                  size="small"
                  color={completionPct === 100 ? 'success' : 'warning'}
                  label={`${completionPct}% complete`}
                />
                <Chip size="small" variant="outlined" label="Available on save" />
              </Stack>
              <LinearProgress
                variant="determinate"
                value={completionPct}
                sx={{ height: 8, borderRadius: 999, bgcolor: alpha(theme.palette.primary.main, 0.1) }}
              />
              <SummaryRow icon={<TagRoundedIcon fontSize="small" />} label="Asset Tag" value={asset.asset_tag} />
              <SummaryRow icon={<Inventory2RoundedIcon fontSize="small" />} label="Serial" value={asset.serial_number} />
              <SummaryRow icon={<CategoryRoundedIcon fontSize="small" />} label="Type" value={titleize(asset.asset_type)} />
              <DetailField
                compact
                label="Condition"
                value={titleize(asset.condition)}
                icon={<VerifiedRoundedIcon fontSize="small" />}
              />
            </Stack>
          </SectionCard>
        </Stack>
      </Box>
    </PageLayout>
  );
}
